import React, { useMemo, useState } from 'react';
import { Alert, Box, Grid, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import RecordsDialog from '../analytics/owner/RecordsDialog';
import { ChartCard, DrillLink, Section, StatTile } from '../analytics/owner/parts';
import { AXIS, GRID, INK, NEUTRAL, SERIES } from '../analytics/chartTheme';
import { formatPct, share, singaporeToday, type DrillSpec, type ExecTile } from '../analytics/owner/ownerData';
import { shiftDays } from './opsHealth';
import { periodText } from './opsHistory';
import {
  confidenceRows,
  confidenceVerdict,
  veryLowNote,
  formatGap,
  MIN_LINES,
  PRICING_RANGES,
  pricingDrill,
  seriesRows,
  sourceLabel,
  TARGET_PCT,
  TOLERANCE_TEXT,
  type PricingAccuracy,
  type PricingDays,
} from './pricingAccuracy';
import { FirstLoad, RangeToggle, StaleNotice } from './trackingParts';
import { useRpc } from './useRpc';

const nf = (n: number | null | undefined) => (n == null ? '-' : n.toLocaleString('en-SG'));
const hideXs = { display: { xs: 'none', sm: 'table-cell' } };

/** A share as a thin bar plus the figure, so a column of rates reads at a glance. */
const RateBar: React.FC<{ pct: number | null; faint?: boolean }> = ({ pct, faint }) => (
  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
    <Box sx={{ width: 64, display: { xs: 'none', md: 'block' } }} aria-hidden>
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        sx={{ height: 6, borderRadius: 3, bgcolor: '#f0efec', '& .MuiLinearProgress-bar': { bgcolor: faint ? NEUTRAL : SERIES.system } }}
      />
    </Box>
    <Box component="span" sx={{ minWidth: 40, textAlign: 'right', color: faint ? INK.muted : undefined }}>
      {formatPct(pct)}
    </Box>
  </Stack>
);

const PricingAccuracyTab: React.FC = () => {
  const [days, setDays] = useState<PricingDays>(90);
  const [drill, setDrill] = useState<DrillSpec | null>(null);
  const today = singaporeToday();
  const range = useMemo(() => ({ from: shiftDays(today, -(days - 1)), to: today }), [days, today]);
  const { data, error, loading, reload } = useRpc<PricingAccuracy>('get_pricing_accuracy', { p_from: range.from, p_to: range.to });
  const series = useMemo(() => (data ? seriesRows(data) : []), [data]);
  const confidence = useMemo(() => (data ? confidenceRows(data) : []), [data]);

  const header = (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
      <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
        How often the price that went out was the system’s suggestion, on quotes sent from the system. The same figures as Analytics &gt; Pricing quality for
        the same dates.
      </Typography>
      <RangeToggle label="pricing" value={days} options={PRICING_RANGES} onChange={setDays} />
    </Stack>
  );

  if (!data) {
    return (
      <Box>
        {header}
        <FirstLoad error={error} what="the pricing accuracy figures" onRetry={reload} tiles={3} />
      </Box>
    );
  }

  const { from, to } = data.range;
  const t = data.totals;
  const span = periodText(from, to);
  const unpriced = Math.max(0, t.quoted_lines - t.priced);
  const verdict = confidenceVerdict(confidence);
  const veryLow = veryLowNote(confidence);
  const stale = loading && (from !== range.from || to !== range.to);
  const grainWord = data.grain === 'week' ? 'week' : 'day';

  const tiles: ExecTile[] = [
    {
      id: 'accuracy',
      label: 'Sent at the system’s price',
      value: formatPct(share(t.within, t.priced)),
      detail: `${nf(t.within)} of ${nf(t.priced)} lines that had a system price went out within tolerance. Target ${TARGET_PCT}%.`,
      drill: pricingDrill('Lines sent with a system price', from, to, {}, 'Each line on a quote sent from the system that carried a system suggestion. Within tolerance: see the rule on the page.'),
    },
    {
      id: 'coverage',
      label: 'Lines the system priced',
      value: formatPct(share(t.priced, t.quoted_lines)),
      detail: `${nf(t.priced)} of ${nf(t.quoted_lines)} lines sent at a price had a system suggestion. The records list the ${nf(unpriced)} priced by hand only.`,
      drill: pricingDrill('Lines sent with no system price', from, to, { priced: false }, 'Lines sent at a price on quotes from the system where the system offered no suggestion.'),
    },
    {
      id: 'gap',
      label: 'Median gap to the price sent',
      value: formatGap(t.median_gap),
      detail: `Half of the ${nf(t.priced)} priced lines went out within this distance of the system’s price. The records list the ${nf(t.priced - t.within)} sent at a different price.`,
      drill: pricingDrill('Lines sent at a different price', from, to, { within: false }),
    },
  ];

  return (
    <Box>
      {header}
      <StaleNotice error={error} onRetry={reload} />
      {stale && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} aria-live="polite">
          Loading…
        </Typography>
      )}

      {t.quoted_lines === 0 ? (
        <Alert severity="info">No quotes with priced lines were sent from the system {span}.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
            {tiles.map((tile) => (
              <StatTile key={tile.id} tile={tile} onDrill={setDrill} />
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 900 }}>
            {span}, quotes sent from the system. {TOLERANCE_TEXT}
          </Typography>

          <Section id="pricing-trend" title={`By ${grainWord}`}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <ChartCard
                  title={`Sent at the system’s price, by ${grainWord}`}
                  subtitle={`The dashed line is the ${TARGET_PCT}% target. A ${grainWord} with fewer than ${MIN_LINES} priced lines is drawn as a hollow dot and not joined to the line: too few to judge.`}
                  height={260}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={GRID} />
                      <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
                      <YAxis tick={AXIS} tickLine={false} axisLine={false} domain={[0, 100]} width={44} tickFormatter={(v: number) => `${v}%`} />
                      <ReferenceLine y={TARGET_PCT} stroke={INK.secondary} strokeDasharray="4 4" label={{ value: `${TARGET_PCT}% target`, position: 'insideTopRight', fontSize: 11, fill: INK.secondary }} />
                      <Tooltip
                        formatter={(value, _name, item) => [
                          `${formatPct(Number(value), 1)} - ${item.payload.within} of ${item.payload.lines} priced lines${item.payload.lines < MIN_LINES ? ' (too few to judge)' : ''} · median gap ${formatGap(item.payload.gap)}`,
                          'Sent at the system’s price',
                        ]}
                      />
                      <Line type="linear" dataKey="pct" name="Sent at the system’s price" stroke={SERIES.system} strokeWidth={2} dot={{ r: 3, fill: SERIES.system }} activeDot={{ r: 6 }} connectNulls={false} />
                      <Line
                        type="linear"
                        dataKey="pctSmall"
                        name="Too few lines"
                        stroke="none"
                        dot={{ r: 3, fill: '#fff', stroke: NEUTRAL, strokeWidth: 1.5 }}
                        activeDot={{ r: 5, fill: '#fff', stroke: INK.secondary }}
                        legendType="none"
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={5}>
                <ChartCard title={`Lines sent at a price, by ${grainWord}`} subtitle="With and without a system suggestion. Blue lines are the ones the accuracy chart is worked out from." height={260}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke={GRID} />
                      <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} minTickGap={16} />
                      <YAxis tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} width={40} />
                      <Tooltip cursor={{ fill: 'rgba(42,120,214,0.08)' }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="lines" name="With a system price" stackId="l" fill={SERIES.system} maxBarSize={22} />
                      <Bar dataKey="unpriced" name="Priced by hand only" stackId="l" fill={NEUTRAL} radius={[3, 3, 0, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>
          </Section>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Section id="pricing-source" title="By price source" subtitle="Where the system’s suggestion came from. Select a source for its lines, or the changed count for the lines sent at a different price.">
                <Paper variant="outlined">
                  <TableContainer>
                    <Table size="small" aria-label="Accuracy by price source">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Priced lines
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            At system price
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, ...hideXs }}>
                            Changed
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, ...hideXs }}>
                            Median gap
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.by_source.map((s) => {
                          const label = sourceLabel(s.source);
                          const small = s.lines < MIN_LINES;
                          return (
                            <TableRow key={s.source ?? 'none'} hover>
                              <TableCell>
                                <DrillLink spec={s.source ? pricingDrill(label, from, to, { source: s.source }) : null} onDrill={setDrill}>
                                  {label}
                                </DrillLink>
                              </TableCell>
                              <TableCell align="right">{nf(s.lines)}</TableCell>
                              <TableCell align="right">
                                <RateBar pct={share(s.within, s.lines)} faint={small} />
                              </TableCell>
                              <TableCell align="right" sx={hideXs}>
                                <DrillLink spec={s.source && s.lines > s.within ? pricingDrill(`${label}: sent at a different price`, from, to, { source: s.source, within: false }) : null} onDrill={setDrill} align="right">
                                  {nf(s.lines - s.within)}
                                </DrillLink>
                              </TableCell>
                              <TableCell align="right" sx={{ ...hideXs, color: small ? INK.muted : undefined }}>
                                {formatGap(s.median_gap)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Section>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Section
                id="pricing-confidence"
                title="Does confidence mean anything?"
                subtitle="Each suggestion carries a confidence label. If the label is useful, high-confidence prices should go out unchanged more often than low ones."
              >
                {verdict && (
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    {verdict}
                  </Typography>
                )}
                <Paper variant="outlined">
                  <TableContainer>
                    <Table size="small" aria-label="Accuracy by confidence">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, ...hideXs }}>
                            Lines sent
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Priced
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            At system price
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, ...hideXs }}>
                            Median gap
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {confidence.map((c) => (
                          <TableRow key={c.confidence} hover>
                            <TableCell>
                              <DrillLink spec={c.quoted ? pricingDrill(`${c.label} confidence`, from, to, { confidence: c.confidence }) : null} onDrill={setDrill}>
                                {c.label}
                              </DrillLink>
                            </TableCell>
                            <TableCell align="right" sx={hideXs}>
                              {nf(c.quoted)}
                            </TableCell>
                            <TableCell align="right">{nf(c.lines)}</TableCell>
                            <TableCell align="right">
                              <RateBar pct={c.pct} faint={c.small} />
                            </TableCell>
                            <TableCell align="right" sx={{ ...hideXs, color: c.small ? INK.muted : undefined }}>
                              {formatGap(c.gap)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                  Grey bars rest on fewer than {MIN_LINES} priced lines. “Not recorded” is a suggestion saved without a confidence label.
                  {veryLow ? ` ${veryLow}` : ''}
                </Typography>
              </Section>
            </Grid>
          </Grid>
        </>
      )}

      <RecordsDialog spec={drill} onClose={() => setDrill(null)} />
    </Box>
  );
};

export default PricingAccuracyTab;
